import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { callApi } from "../../../lib/apiCaller"; // adjust path as needed

// ✅ Async thunk to fetch categories from contentBaseUrl
export const fetchCategories = createAsyncThunk(
  "categories/fetchCategories",

  async (_, ThunkAPI) => {
    try {
      const res = await callApi({
        key: "FETCH_CUSTOMER_CATEGORIES",
        method: "GET",
        useContent: true,
      });

      return res?.data;
    } catch (error) {
      return ThunkAPI.rejectWithValue(
        error?.message || "Failed to fetch categories",
      );
    }
  },
);
export const fetchCategoriesOption = createAsyncThunk(
  "categories/fetchCategoriesOption",

  async ({ id, type }, ThunkAPI) => {
    try {
      const res = await callApi({
        key: "CATEGORY_OPTION",
        method: "GET",
        pathParam: id,
        useContent: true,
      });

      return { data: res?.data, type };
    } catch (error) {
      return ThunkAPI.rejectWithValue(
        error?.message || "Failed to fetch categories",
      );
    }
  },
  {
    // Prevent duplicate requests if data already exists for this type
    condition: ({ id, type }, { getState }) => {
      const state = getState().category;
      // Check if data already exists for this specific type
      if (type === "normal" && state.singleSubcategory?.length > 0)
        return false;
      if (type === "deep" && state.deepCleaning?.length > 0) return false;
      if (type === "hourly" && state.hourlyService?.length > 0) return false;
      if (type === "carwash" && state.carwash?.length > 0) return false;
      return true;
    },
  },
);
export const fetchCategoryById = createAsyncThunk(
  "categories/fetchCategoryById",
  async (id, ThunkAPI) => {
    try {
      const res = await callApi({
        key: "FETCH_CUSTOMER_CATEGORIES",
        method: "GET",
        pathParam: id,
        useContent: true,
      });
      return res?.data;
    } catch (error) {
      return ThunkAPI.rejectWithValue(
        error?.message || "Failed to fetch category by ID",
      );
    }
  },
);
export const fetchSubCategoryById = createAsyncThunk(
  "categories/fetchSubCategoryById",
  async (id, ThunkAPI) => {
    try {
      const res = await callApi({
        key: "FETCH_PARTICULAR_CATEGORY",
        method: "GET",
        pathParam: id,
        useContent: true,
      });

      return res?.data;
    } catch (error) {
      return ThunkAPI.rejectWithValue(
        error?.message || "Failed to fetch category by ID",
      );
    }
  },
);
export const fetchHourlyServiceById = createAsyncThunk(
  "categories/fetchHourlyServiceById",
  async (id, ThunkAPI) => {
    try {
      const res = await callApi({
        key: "FETCH_PARTICULAR_CATEGORY",
        method: "GET",
        pathParam: id,
        useContent: true,
      });

      return res?.data;
    } catch (error) {
      return ThunkAPI.rejectWithValue(
        error?.message || "Failed to fetch category by ID",
      );
    }
  },
);
export const fetchDeepCleaningId = createAsyncThunk(
  "categories/fetchDeepCleaningId",
  async (id, ThunkAPI) => {
    try {
      const res = await callApi({
        key: "FETCH_PARTICULAR_CATEGORY",
        method: "GET",
        pathParam: id,
        useContent: true,
      });

      return res?.data;
    } catch (error) {
      return ThunkAPI.rejectWithValue(
        error?.message || "Failed to fetch category by ID",
      );
    }
  },
);
export const fetchCarWash = createAsyncThunk(
  "categories/fetchCarWash",
  async (id, ThunkAPI) => {
    try {
      const res = await callApi({
        key: "FETCH_PARTICULAR_CATEGORY",
        method: "GET",
        pathParam: id,
        useContent: true,
      });

      return res?.data;
    } catch (error) {
      return ThunkAPI.rejectWithValue(
        error?.message || "Failed to fetch category by ID",
      );
    }
  },
);
export const fetchSubCategoryByIdCoupons = createAsyncThunk(
  "categories/fetchSubCategoryByIdCoupons",
  async (id, ThunkAPI) => {
    try {
      const res = await callApi({
        key: "FETCH_COUPONS",
        method: "GET",
        pathParam: id,
        useContent: true,
      });

      return res?.data;
    } catch (error) {
      return ThunkAPI.rejectWithValue(
        error?.message || "Failed to fetch category by ID",
      );
    }
  },
  {
    // Prevent duplicate requests if coupons already loaded
    condition: (id, { getState }) => {
      const state = getState().category;
      // Don't fetch if coupons already exist
      if (
        state.coupons &&
        Array.isArray(state.coupons) &&
        state.coupons.length > 0
      ) {
        return false;
      }
      return true;
    },
  },
);
export const fetchServiceOfDay = createAsyncThunk(
  "categories/fetchServiceOfDay",
  async (_, ThunkAPI) => {
    try {
      const res = await callApi({
        key: "FETCH_SERVICE_OF_THE_DAY",
        method: "GET",
        useContent: true,
      });

      return res?.data;
    } catch (error) {
      return ThunkAPI.rejectWithValue(
        error?.message || "Failed to fetch service of the day",
      );
    }
  },
);
export const fetchOffers = createAsyncThunk(
  "categories/fetchOffers",
  async (_, ThunkAPI) => {
    try {
      const res = await callApi({
        key: "FETCH_CUSTOMER_OFFERS",
        method: "GET",
        useContent: true,
      });

      return res?.data;
    } catch (error) {
      return ThunkAPI.rejectWithValue(
        error?.message || "Failed to fetch service of the day",
      );
    }
  },
);

// ✅ Slice
const categoriesSlice = createSlice({
  name: "categories",
  initialState: {
    data: [],
    single: null,
    coupons: null,
    singleSubcategory: null,
    deepCleaning: null,
    carwash: null,
    serviceOfDay: null,
    hourlyService: null,
    loading: false,
    loadingSegmnts: false,
    offers: null,
    selectedCategoryId: "",
    selectedCategoryName: "",
    selectedSubCategoryId: "",
    error: null,
  },
  reducers: {
    resetCategories(state) {
      state.data = [];
      state.single = null;
      state.coupons = null;
      state.loading = false;
      state.loadingSegmnts = false;
      state.offers = null;
      state.error = null;
      state.hourlyService = null;
      state.serviceOfDay = null;
      state.singleSubcategory = null;
      state.deepCleaning = null;
      state.carwash = null;
      state.selectedCategoryId = "";
      state.selectedCategoryName = "";
      state.categoryOption = null;
      state.selectedSubCategoryId = "";
    },
    setSelectedCategory(state, action) {
      state.selectedCategoryId = action.payload.categoryId;
      state.selectedCategoryName = action.payload.categoryName;
    },
    setSelectedSubCategory(state, action) {
      state.selectedSubCategoryId = action.payload.subCategoryId;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchCategories
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch categories";
      })
      // fetchCategoryById
      .addCase(fetchCategoryById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategoryById.fulfilled, (state, action) => {
        state.loading = false;

        state.single = action.payload.data;
      })
      .addCase(fetchCategoryById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch category by ID";
      })
      // fetchDeepCleaningId
      .addCase(fetchDeepCleaningId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDeepCleaningId.fulfilled, (state, action) => {
        state.loading = false;

        state.deepCleaning = action.payload.data;
      })
      .addCase(fetchDeepCleaningId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch category by ID";
      })

      .addCase(fetchSubCategoryById.pending, (state) => {
        state.loadingSegmnts = true;
        state.error = null;
      })
      .addCase(fetchSubCategoryById.fulfilled, (state, action) => {
        state.loadingSegmnts = false;

        state.singleSubcategory = action.payload.data;
      })
      .addCase(fetchSubCategoryById.rejected, (state, action) => {
        state.loadingSegmnts = false;
        state.error = action.payload || "Failed to fetch category by ID";
      })
      .addCase(fetchSubCategoryByIdCoupons.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSubCategoryByIdCoupons.fulfilled, (state, action) => {
        state.loading = false;

        state.coupons = action.payload.value;
      })
      .addCase(fetchSubCategoryByIdCoupons.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch category by ID";
      })
      .addCase(fetchServiceOfDay.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchServiceOfDay.fulfilled, (state, action) => {
        state.loading = false;
        state.serviceOfDay = action.payload.data;
      })
      .addCase(fetchServiceOfDay.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch service of the day";
      })
      .addCase(fetchOffers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOffers.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.offers = action.payload.data;
      })
      .addCase(fetchOffers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchHourlyServiceById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHourlyServiceById.fulfilled, (state, action) => {
        state.loading = false;
        state.hourlyService = action.payload.data;
      })
      .addCase(fetchHourlyServiceById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch hourly service by ID";
      })
      .addCase(fetchCategoriesOption.pending, (state) => {
        state.loadingSegmnts = true;
        state.error = null;
      })
      .addCase(fetchCategoriesOption.fulfilled, (state, action) => {
        state.loadingSegmnts = false;
        const { data, type } = action.payload;
        // Try both data structures - some APIs return data.data, others just data
        const categoryData = data?.data ?? data;

        // Store in appropriate state based on type
        if (type === "normal") {
          state.singleSubcategory = categoryData;
        } else if (type === "deep") {
          state.deepCleaning = categoryData;
        } else if (type === "hourly") {
          state.hourlyService = categoryData;
        } else if (type === "carwash") {
          state.carwash = categoryData;
        }
      })
      .addCase(fetchCategoriesOption.rejected, (state, action) => {
        state.loadingSegmnts = false;
        state.error = action.payload || "Failed to fetch category options";
      });
  },
});

export const { resetCategories, setSelectedCategory, setSelectedSubCategory } =
  categoriesSlice.actions;
export default categoriesSlice.reducer;
